<script setup>
import { computed, ref, useAttrs, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { useAlert } from 'dashboard/composables';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { useCallsStore } from 'dashboard/stores/calls';
import WhatsappVoiceClient from 'dashboard/api/channel/voice/whatsappVoiceClient';
import VoiceAPI from 'dashboard/api/channel/voice/voiceAPIClient';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  phone: { type: String, default: '' },
  contactId: { type: [String, Number], required: true },
  label: { type: String, default: '' },
  icon: { type: [String, Object, Function], default: '' },
  size: { type: String, default: 'sm' },
  tooltipLabel: { type: String, default: '' },
});

defineOptions({ inheritAttrs: false });
const attrs = useAttrs();
const route = useRoute();
const router = useRouter();
const store = useStore();

const { t } = useI18n();

const dialogRef = ref(null);
const permissionDialogRef = ref(null);
const callProgressDialogRef = ref(null);
const permissionRequestBody = ref('');
const pendingPermissionInboxId = ref(null);
const permissionData = ref(null);

// Call progress state
const CALL_STEPS = {
  CHECKING_PERMISSION: 'checking_permission',
  PREPARING_CALL: 'preparing_call',
  PLACING_CALL: 'placing_call',
  DONE: 'done',
  ERROR: 'error',
};

const callStep = ref(null);
const callError = ref('');
const errorStepKey = ref(null);

const inboxesList = useMapGetter('inboxes/getInboxes');
const contactsUiFlags = useMapGetter('contacts/getUIFlags');

const voiceInboxes = computed(() =>
  (inboxesList.value || []).filter(
    inbox =>
      inbox.channel_type === INBOX_TYPES.VOICE ||
      inbox.channel_type === INBOX_TYPES.WHATSAPP
  )
);
const hasVoiceInboxes = computed(() => voiceInboxes.value.length > 0);

const shouldRender = computed(() => hasVoiceInboxes.value && !!props.phone);

const isInitiatingCall = computed(() => {
  return contactsUiFlags.value?.isInitiatingCall || false;
});

const TIME_PERIOD_LABELS = { PT24H: '24h', P7D: '7 days' };

const formatLimitsText = computed(() => {
  const limits = permissionData.value?.permission_request_limits?.limits || [];
  if (!limits.length) return '';

  return limits
    .map(l => {
      const remaining = Math.max(0, l.max_allowed - l.current_usage);
      const label = TIME_PERIOD_LABELS[l.time_period] || l.time_period;
      return `${remaining}/${l.max_allowed} (${label})`;
    })
    .join(' · ');
});

const permissionStatusText = computed(() => {
  const data = permissionData.value;
  if (!data) return '';

  if (data.status === 'temporary' && data.expiration_time) {
    const expiry = new Date(data.expiration_time * 1000).toLocaleString();
    return t('CONTACT_PANEL.CALL_PERMISSION.PERMISSION_STATUS_TEMPORARY', {
      time: expiry,
    });
  }
  if (data.status === 'permanent') {
    return t('CONTACT_PANEL.CALL_PERMISSION.PERMISSION_STATUS_PERMANENT');
  }
  return '';
});

// Progress step helpers
const progressSteps = computed(() => [
  {
    key: CALL_STEPS.CHECKING_PERMISSION,
    label: t('CONTACT_PANEL.CALL_PROGRESS.CHECKING_PERMISSION'),
  },
  {
    key: CALL_STEPS.PREPARING_CALL,
    label: t('CONTACT_PANEL.CALL_PROGRESS.PREPARING_CALL'),
  },
  {
    key: CALL_STEPS.PLACING_CALL,
    label: t('CONTACT_PANEL.CALL_PROGRESS.PLACING_CALL'),
  },
]);

const stepOrder = [
  CALL_STEPS.CHECKING_PERMISSION,
  CALL_STEPS.PREPARING_CALL,
  CALL_STEPS.PLACING_CALL,
];

const getStepStatus = stepKey => {
  if (callStep.value === CALL_STEPS.ERROR) {
    const currentIdx = stepOrder.indexOf(errorStepKey.value);
    const stepIdx = stepOrder.indexOf(stepKey);
    if (stepIdx < currentIdx) return 'done';
    if (stepIdx === currentIdx) return 'error';
    return 'pending';
  }

  if (callStep.value === CALL_STEPS.DONE) return 'done';

  const currentIdx = stepOrder.indexOf(callStep.value);
  const stepIdx = stepOrder.indexOf(stepKey);

  if (stepIdx < currentIdx) return 'done';
  if (stepIdx === currentIdx) return 'active';
  return 'pending';
};

const navigateToConversation = conversationId => {
  const accountId = route.params.accountId;
  if (conversationId && accountId) {
    const path = frontendURL(
      conversationUrl({
        accountId,
        id: conversationId,
      })
    );
    router.push({ path });
  }
};

const checkAndStartWhatsappCall = async inbox => {
  try {
    const permission = await VoiceAPI.checkCallPermission({
      inboxId: inbox.id,
      userWaId: props.phone,
    });

    permissionData.value = permission;

    if (permission.can_call) {
      return true;
    }

    if (permission.status === 'no_permission') {
      if (permission.can_request_permission) {
        pendingPermissionInboxId.value = inbox.id;
        permissionRequestBody.value = '';
        callProgressDialogRef.value?.close();
        await nextTick();
        permissionDialogRef.value?.open();
        return false;
      }
      throw new Error(t('CONTACT_PANEL.CALL_PERMISSION.NO_PERMISSION'));
    }

    const resetTime =
      permission.call_limits?.limits?.[0]?.limit_expiration_time;
    if (resetTime) {
      const resetDate = new Date(resetTime * 1000).toLocaleString();
      throw new Error(
        t('CONTACT_PANEL.CALL_PERMISSION.RATE_LIMITED_UNTIL', {
          time: resetDate,
        })
      );
    }
    throw new Error(t('CONTACT_PANEL.CALL_PERMISSION.RATE_LIMITED'));
  } catch (error) {
    if (
      error.message?.includes('permission') ||
      error.message?.includes('limit') ||
      error.message?.includes('Permission') ||
      error.message?.includes('Limit')
    ) {
      throw error;
    }
    return true;
  }
};

const sendPermissionRequest = async () => {
  try {
    await VoiceAPI.requestCallPermission({
      inboxId: pendingPermissionInboxId.value,
      userWaId: props.phone,
      body: permissionRequestBody.value || undefined,
    });
    useAlert(t('CONTACT_PANEL.CALL_PERMISSION.REQUEST_SENT'));
  } catch {
    useAlert(t('CONTACT_PANEL.CALL_PERMISSION.REQUEST_FAILED'));
  } finally {
    permissionDialogRef.value?.close();
    pendingPermissionInboxId.value = null;
  }
};

const startCall = async inboxId => {
  if (isInitiatingCall.value) return;

  const inbox = voiceInboxes.value.find(i => i.id === inboxId);
  if (!inbox) return;

  callStep.value = CALL_STEPS.CHECKING_PERMISSION;
  callError.value = '';
  errorStepKey.value = null;
  callProgressDialogRef.value?.open();

  try {
    let webrtcPayload = null;
    if (inbox.channel_type === INBOX_TYPES.WHATSAPP) {
      const canCall = await checkAndStartWhatsappCall(inbox);
      if (!canCall) return;

      callStep.value = CALL_STEPS.PREPARING_CALL;
      try {
        webrtcPayload = await WhatsappVoiceClient.createOffer();
      } catch (micError) {
        if (micError.message === 'MICROPHONE_PERMISSION_DENIED') {
          throw new Error(
            t('CONTACT_PANEL.MICROPHONE_PERMISSION_DENIED')
          );
        }
        if (micError.message === 'MICROPHONE_NOT_FOUND') {
          throw new Error(t('CONTACT_PANEL.MICROPHONE_NOT_FOUND'));
        }
        throw micError;
      }
      if (!webrtcPayload) {
        throw new Error(t('CONTACT_PANEL.CALL_FAILED'));
      }
    } else {
      callStep.value = CALL_STEPS.PREPARING_CALL;
    }

    callStep.value = CALL_STEPS.PLACING_CALL;

    const response = await store.dispatch('contacts/initiateCall', {
      contactId: props.contactId,
      inboxId,
      webrtcPayload,
    });
    const { call_sid: callSid, conversation_id: conversationId } = response;

    const callsStore = useCallsStore();
    callsStore.addCall({
      callSid,
      conversationId,
      inboxId,
      callDirection: 'outbound',
      webrtcPayload,
    });

    callStep.value = CALL_STEPS.DONE;

    setTimeout(() => {
      callProgressDialogRef.value?.close();
      navigateToConversation(response?.conversation_id);
    }, 800);
  } catch (error) {
    errorStepKey.value = callStep.value;
    callStep.value = CALL_STEPS.ERROR;
    callError.value = error?.message || t('CONTACT_PANEL.CALL_FAILED');
  }
};

const dismissProgress = () => {
  callProgressDialogRef.value?.close();
  callStep.value = null;
  callError.value = '';
  errorStepKey.value = null;
};

const onClick = async () => {
  if (voiceInboxes.value.length > 1) {
    dialogRef.value?.open();
    return;
  }
  const [inbox] = voiceInboxes.value;
  await startCall(inbox.id);
};

const onPickInbox = async inbox => {
  dialogRef.value?.close();
  await startCall(inbox.id);
};
</script>

<template>
  <span class="contents">
    <Button
      v-if="shouldRender"
      v-tooltip.top-end="tooltipLabel || null"
      v-bind="attrs"
      :disabled="isInitiatingCall"
      :is-loading="isInitiatingCall"
      :label="label"
      :icon="icon"
      :size="size"
      @click="onClick"
    />

    <!-- Inbox picker dialog -->
    <Dialog
      v-if="shouldRender && voiceInboxes.length > 1"
      ref="dialogRef"
      :title="$t('CONTACT_PANEL.VOICE_INBOX_PICKER.TITLE')"
      show-cancel-button
      :show-confirm-button="false"
      width="md"
    >
      <div class="flex flex-col gap-2">
        <button
          v-for="inbox in voiceInboxes"
          :key="inbox.id"
          type="button"
          class="flex items-center justify-between w-full px-4 py-2 text-left rounded-lg hover:bg-n-alpha-2"
          @click="onPickInbox(inbox)"
        >
          <div class="flex items-center gap-2">
            <span class="i-ri-phone-fill text-n-slate-10" />
            <span class="text-sm text-n-slate-12">{{ inbox.name }}</span>
          </div>
          <span v-if="inbox.phone_number" class="text-xs text-n-slate-10">
            {{ inbox.phone_number }}
          </span>
        </button>
      </div>
    </Dialog>

    <!-- Call Progress Dialog -->
    <Dialog
      v-if="shouldRender"
      ref="callProgressDialogRef"
      :title="$t('CONTACT_PANEL.CALL_PROGRESS.TITLE')"
      :show-confirm-button="callStep === 'error'"
      :confirm-text="$t('CONTACT_PANEL.CALL_PROGRESS.DISMISS')"
      :show-cancel-button="false"
      width="md"
      @confirm="dismissProgress"
    >
      <div class="flex flex-col gap-3 py-1">
        <div
          v-for="step in progressSteps"
          :key="step.key"
          class="flex items-center gap-3"
        >
          <!-- Step icon -->
          <div
            class="flex items-center justify-center rounded-full size-7 shrink-0"
            :class="{
              'bg-n-teal-3': getStepStatus(step.key) === 'done',
              'bg-n-brand-3': getStepStatus(step.key) === 'active',
              'bg-n-ruby-3': getStepStatus(step.key) === 'error',
              'bg-n-alpha-2': getStepStatus(step.key) === 'pending',
            }"
          >
            <Icon
              v-if="getStepStatus(step.key) === 'done'"
              icon="i-ph-check-bold"
              class="size-3.5 text-n-teal-11"
            />
            <Icon
              v-else-if="getStepStatus(step.key) === 'error'"
              icon="i-ph-x-bold"
              class="size-3.5 text-n-ruby-11"
            />
            <span
              v-else-if="getStepStatus(step.key) === 'active'"
              class="size-3.5 border-2 rounded-full animate-spin border-n-brand-11 border-t-transparent"
            />
            <span v-else class="rounded-full size-2 bg-n-slate-8" />
          </div>

          <!-- Step label -->
          <span
            class="text-sm"
            :class="{
              'text-n-teal-11 font-medium': getStepStatus(step.key) === 'done',
              'text-n-slate-12 font-medium':
                getStepStatus(step.key) === 'active',
              'text-n-ruby-11 font-medium': getStepStatus(step.key) === 'error',
              'text-n-slate-9': getStepStatus(step.key) === 'pending',
            }"
          >
            {{ step.label }}
          </span>
        </div>

        <!-- Error message -->
        <div
          v-if="callStep === 'error' && callError"
          class="flex items-start gap-2 p-3 mt-1 rounded-lg bg-n-ruby-2"
        >
          <Icon
            icon="i-ph-warning-circle"
            class="mt-0.5 size-4 text-n-ruby-11 shrink-0"
          />
          <span class="text-sm text-n-ruby-11">{{ callError }}</span>
        </div>

        <!-- Success message -->
        <div
          v-if="callStep === 'done'"
          class="flex items-center gap-2 p-3 mt-1 rounded-lg bg-n-teal-2"
        >
          <Icon
            icon="i-ph-check-circle"
            class="size-4 text-n-teal-11 shrink-0"
          />
          <span class="text-sm font-medium text-n-teal-11">
            {{ $t('CONTACT_PANEL.CALL_PROGRESS.CALL_PLACED') }}
          </span>
        </div>
      </div>
    </Dialog>

    <!-- Permission request dialog -->
    <Dialog
      v-if="shouldRender"
      ref="permissionDialogRef"
      :title="$t('CONTACT_PANEL.CALL_PERMISSION.DIALOG_TITLE')"
      :confirm-text="$t('CONTACT_PANEL.CALL_PERMISSION.SEND_REQUEST')"
      show-cancel-button
      width="md"
      @confirm="sendPermissionRequest"
    >
      <p class="mb-3 text-sm text-n-slate-11">
        {{ $t('CONTACT_PANEL.CALL_PERMISSION.DIALOG_DESCRIPTION') }}
      </p>
      <div
        v-if="permissionStatusText || formatLimitsText"
        class="flex flex-col gap-1 p-2.5 mb-3 rounded-lg bg-n-alpha-1"
      >
        <p v-if="permissionStatusText" class="text-xs text-n-slate-10">
          {{ permissionStatusText }}
        </p>
        <p v-if="formatLimitsText" class="text-xs text-n-slate-10">
          {{
            $t('CONTACT_PANEL.CALL_PERMISSION.REQUESTS_AVAILABLE', {
              usage: formatLimitsText,
            })
          }}
        </p>
      </div>
      <textarea
        v-model="permissionRequestBody"
        class="w-full px-3 py-2 text-sm border rounded-lg resize-none bg-n-alpha-1 border-n-strong text-n-slate-12 placeholder:text-n-slate-9"
        rows="3"
        :placeholder="$t('CONTACT_PANEL.CALL_PERMISSION.BODY_PLACEHOLDER')"
      />
    </Dialog>
  </span>
</template>
